import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Clock,
  Award,
  CheckCircle,
  XCircle,
  User,
  Mail,
  Hash,
  BookOpen,
  Calendar,
  MessageSquare,
  ShieldAlert,
  AlertTriangle,
  BarChart3,
  Ban,
} from 'lucide-react';
import { Card, Badge, Button, StatCard, Modal, ModalFooter, Textarea, Tabs, TabsList, TabsTrigger, TabsContent } from '@azyntor-labs/nexus-ui';
import { attemptsApi, examsApi, gradebookApi, proctoringApi } from '@/api/endpoints';
import type { ViolationStats, RiskScoreResponse, AttemptFullDetail } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { QuestionsBreakdown } from '@/components/attempt-detail/QuestionsBreakdown';
import { ProctoringEvidence } from '@/components/attempt-detail/ProctoringEvidence';
import { PatternsPanel } from '@/components/attempt-detail/PatternsPanel';

interface GradeEntry {
  attempt_id: string;
  student_id: string;
  student_name: string;
  student_email: string;
  score: number;
  total_marks: number;
  percentage: number;
  grade: string;
  passed: boolean;
  feedback?: string;
  submitted_at: string;
  graded_at?: string;
}

export const AttemptDetailPage = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [activeTab, setActiveTab] = useState<string>('overview');

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => attemptsApi.adminCancel(attemptId!, reason),
    onSuccess: () => {
      toast.success('Attempt cancelled — the student can now retake the exam');
      queryClient.invalidateQueries({ queryKey: ['attempt-detail', attemptId] });
      setCancelModalOpen(false);
      setCancelReason('');
    },
    onError: (err: unknown) => {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(anyErr?.response?.data?.message || anyErr?.message || 'Failed to cancel attempt');
    },
  });

  // 1. Fetch attempt data
  const { data: attemptRes, isLoading: attemptLoading } = useQuery({
    queryKey: ['attempt-detail', attemptId],
    queryFn: () => attemptsApi.get(attemptId!),
    enabled: !!attemptId,
  });

  const attempt = attemptRes?.data?.data as any;
  const examId = attempt?.exam_id;

  // 2. Fetch exam details
  const { data: examRes, isLoading: examLoading } = useQuery({
    queryKey: ['exam-detail', examId],
    queryFn: () => examsApi.get(examId),
    enabled: !!examId,
  });

  const exam = examRes?.data?.data as any;

  // 3. Fetch grade data
  const { data: gradesRes } = useQuery({
    queryKey: ['exam-grades-for-attempt', examId],
    queryFn: () => gradebookApi.getExamGrades(examId, { per_page: 200 }),
    enabled: !!examId,
  });

  const grades: GradeEntry[] = Array.isArray(gradesRes?.data?.data)
    ? (gradesRes.data.data as GradeEntry[])
    : [];
  const gradeEntry = grades.find((g) => g.attempt_id === attemptId);

  // 4. Fetch risk score
  const { data: riskRes, isLoading: riskLoading } = useQuery({
    queryKey: ['risk-score', attemptId],
    queryFn: () => proctoringApi.getRiskScore(attemptId!),
    enabled: !!attemptId,
    staleTime: 30000,
  });

  const riskScore = riskRes?.data?.data as RiskScoreResponse | undefined;

  // 5. Fetch violation stats
  const { data: violationRes, isLoading: violationLoading } = useQuery({
    queryKey: ['violation-stats', attemptId],
    queryFn: () => proctoringApi.getViolationStats(attemptId!),
    enabled: !!attemptId,
    staleTime: 30000,
  });

  const violationStats = violationRes?.data?.data as ViolationStats | undefined;

  // 6. Unified enhanced detail (questions + proctoring evidence + patterns)
  const { data: fullDetailRes } = useQuery({
    queryKey: ['attempt-full-detail', attemptId],
    queryFn: () => attemptsApi.getFullDetail(attemptId!),
    enabled: !!attemptId,
    staleTime: 30000,
  });
  const fullDetail = fullDetailRes?.data?.data as AttemptFullDetail | undefined;

  // Compute display values
  const studentName = gradeEntry?.student_name || attempt?.student_name || 'Student';
  const studentEmail = gradeEntry?.student_email || attempt?.student_email || '';
  const studentId = gradeEntry?.student_id || attempt?.student_id || '';

  const score = gradeEntry?.score ?? attempt?.score ?? 0;
  const totalMarks = gradeEntry?.total_marks ?? exam?.total_marks ?? 100;
  const percentage = gradeEntry?.percentage ?? attempt?.percentage ?? (totalMarks > 0 ? (score / totalMarks) * 100 : 0);
  const grade = gradeEntry?.grade || calculateGrade(percentage);
  const passed = gradeEntry?.passed ?? (attempt?.result === 'pass' || attempt?.result === 'passed');

  const timeTaken = attempt?.end_time && attempt?.start_time
    ? Math.floor((new Date(attempt.end_time).getTime() - new Date(attempt.start_time).getTime()) / 1000)
    : 0;
  const mins = Math.floor(timeTaken / 60);
  const secs = timeTaken % 60;

  const riskLevelColor = (level?: string) => {
    switch (level) {
      case 'critical': return 'bg-red-500/20 text-red-400';
      case 'high': return 'bg-orange-500/20 text-orange-400';
      case 'medium': return 'bg-amber-500/20 text-amber-400';
      default: return 'bg-emerald-500/20 text-emerald-400';
    }
  };

  const riskBadgeVariant = (level?: string): 'danger' | 'warning' | 'info' | 'success' => {
    switch (level) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'warning';
      default: return 'success';
    }
  };

  const isLoading = attemptLoading || examLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="text-center py-20">
        <p className="text-white/50">Attempt not found</p>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
          onClick={() => navigate(examId ? `/teacher/exam-results/exam/${examId}` : '/teacher/exam-results')}
          className="mt-4"
        >
          Back to Results
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(examId ? `/teacher/exam-results/exam/${examId}` : '/teacher/exam-results')}
          >
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">Attempt Details</h1>
            <p className="text-white/50 mt-1">{studentName}</p>
          </div>
        </div>
        {attempt.status !== 'cancelled' && attempt.status !== 'graded' && (
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Ban className="w-4 h-4" />}
            onClick={() => setCancelModalOpen(true)}
            className="!text-red-300 !border-red-500/40 hover:!bg-red-500/10"
          >
            Cancel attempt
          </Button>
        )}
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          title="Score"
          value={`${percentage.toFixed(1)}%`}
          icon={<BarChart3 className="w-4 h-4" />}
          color="teal"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Grade"
          value={grade || '-'}
          icon={<Award className="w-4 h-4" />}
          color="purple"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Result"
          value={passed ? 'Passed' : 'Failed'}
          icon={passed ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          color={passed ? 'green' : 'red'}
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Time Taken"
          value={timeTaken > 0 ? `${mins}m ${secs}s` : '-'}
          icon={<Clock className="w-4 h-4" />}
          color="blue"
          className="!p-3 [&_h3]:!text-xl"
        />
      </div>

      <Card className="bg-[#12121a]/80 border-white/[0.06] backdrop-blur-sm !p-0">
      <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="overview" className="w-full">
        <TabsList className="w-full border-b border-white/[0.06] bg-transparent p-0">
          <TabsTrigger value="overview" className="flex-1">
            <span className="inline-flex items-center justify-center gap-2">
              <User className="w-4 h-4" />
              Overview
            </span>
          </TabsTrigger>
          <TabsTrigger value="questions" className="flex-1">
            <span className="inline-flex items-center justify-center gap-2">
              <BookOpen className="w-4 h-4" />
              Questions{fullDetail ? ` (${fullDetail.answers.length})` : ''}
            </span>
          </TabsTrigger>
          <TabsTrigger value="proctoring" className="flex-1">
            <span className="inline-flex items-center justify-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              Proctoring{fullDetail && fullDetail.violations.length > 0 ? ` (${fullDetail.violations.length})` : ''}
            </span>
          </TabsTrigger>
          <TabsTrigger value="patterns" className="flex-1">
            <span className="inline-flex items-center justify-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Patterns
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="p-5 space-y-6">

      {/* Student Info Card */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Student Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-teal-500/15 flex items-center justify-center">
                <User className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <p className="text-xs text-white/40">Name</p>
                <p className="text-sm font-medium text-white/90">{studentName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-white/40">Email</p>
                <p className="text-sm font-medium text-white/90">{studentEmail || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-purple-500/15 flex items-center justify-center">
                <Hash className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-white/40">Student ID</p>
                <p className="text-sm font-medium text-white/90 font-mono">{studentId ? studentId.slice(0, 8) + '...' : '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Exam Info Card */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Exam Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-white/40">Exam Title</p>
              <p className="text-sm font-medium text-white/90">{exam?.title || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-white/40">Total Marks</p>
              <p className="text-sm font-medium text-white/90">{totalMarks}</p>
            </div>
            <div>
              <p className="text-xs text-white/40">Passing Marks</p>
              <p className="text-sm font-medium text-white/90">{exam?.passing_marks ?? '-'}</p>
            </div>
            <div>
              <p className="text-xs text-white/40">Submitted</p>
              <p className="text-sm font-medium text-white/90 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-white/40" />
                {gradeEntry?.submitted_at
                  ? new Date(gradeEntry.submitted_at).toLocaleString()
                  : attempt?.end_time
                    ? new Date(attempt.end_time).toLocaleString()
                    : '-'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Instructor Feedback Card */}
      {(gradeEntry?.feedback || attempt?.feedback) && (
        <Card>
          <div className="p-5">
            <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Instructor Feedback
            </h3>
            <p className="text-sm text-white/80 leading-relaxed">
              {gradeEntry?.feedback || attempt?.feedback}
            </p>
          </div>
        </Card>
      )}

      {/* Risk Assessment Card */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            Risk Assessment
          </h3>
          {riskLoading ? (
            <p className="text-sm text-white/40">Loading risk data...</p>
          ) : riskScore ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Badge variant={riskBadgeVariant(riskScore.risk_level)} size="sm">
                  {riskScore.risk_level?.toUpperCase() || 'N/A'}
                </Badge>
                <div>
                  <span className="text-xs text-white/40">Total Score:</span>
                  <span className="ml-2 text-lg font-bold text-white/90">{riskScore.total_score?.toFixed(0)}</span>
                </div>
              </div>
              {riskScore.factors && riskScore.factors.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-2">Risk Factors</p>
                  <div className="space-y-2">
                    {riskScore.factors.map((factor, i) => (
                      <div key={i} className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-2.5">
                        <span className="text-sm text-white/70">{factor.factor}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-white/40">{factor.count}x</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${riskLevelColor(
                            factor.score >= 80 ? 'critical' :
                            factor.score >= 50 ? 'high' :
                            factor.score >= 25 ? 'medium' : 'low'
                          )}`}>
                            {factor.score?.toFixed(0)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-white/40">No risk data available for this attempt</p>
          )}
        </div>
      </Card>

      {/* Violation Stats Card */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Violation Statistics
          </h3>
          {violationLoading ? (
            <p className="text-sm text-white/40">Loading violation data...</p>
          ) : violationStats && violationStats.total_violations > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Severity */}
              <div>
                <p className="text-xs text-white/40 mb-3">
                  By Severity ({violationStats.total_violations} total)
                </p>
                <div className="space-y-2">
                  {Object.entries(violationStats.by_severity || {}).map(([severity, count]) => (
                    <div key={severity} className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                        severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {severity}
                      </span>
                      <span className="text-sm text-white/70">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-white/40 border-t border-white/[0.06] pt-2">
                  <span>Reviewed: {violationStats.reviewed_count}</span>
                  <span>False Positives: {violationStats.false_positive_count}</span>
                  <span>Unreviewed: {violationStats.unreviewed_count}</span>
                </div>
              </div>

              {/* By Type */}
              <div>
                <p className="text-xs text-white/40 mb-3">By Type</p>
                <div className="space-y-2">
                  {Object.entries(violationStats.by_type || {}).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-sm text-white/70">{type.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full"
                            style={{
                              width: `${Math.min(100, (count / violationStats.total_violations) * 100)}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm text-white/70 w-6 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-white/30" />
              <p className="text-sm text-white/40">No violations recorded for this attempt</p>
            </div>
          )}
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="questions" className="p-5">
          {fullDetail ? (
            <QuestionsBreakdown answers={fullDetail.answers} timing={fullDetail.timing_stats} />
          ) : (
            <div className="p-8 text-center text-white/40 text-sm">Loading…</div>
          )}
        </TabsContent>

        <TabsContent value="proctoring" className="p-5">
          {fullDetail ? (
            <ProctoringEvidence
              snapshots={fullDetail.snapshots}
              violations={fullDetail.violations}
              startTime={fullDetail.attempt.start_time}
              endTime={fullDetail.attempt.end_time}
            />
          ) : (
            <div className="p-8 text-center text-white/40 text-sm">Loading…</div>
          )}
        </TabsContent>

        <TabsContent value="patterns" className="p-5">
          {fullDetail ? (
            <PatternsPanel timing={fullDetail.timing_stats} patterns={fullDetail.patterns} />
          ) : (
            <div className="p-8 text-center text-white/40 text-sm">Loading…</div>
          )}
        </TabsContent>
      </Tabs>
      </Card>

      <Modal
        isOpen={cancelModalOpen}
        onClose={() => {
          if (!cancelMutation.isPending) {
            setCancelModalOpen(false);
            setCancelReason('');
          }
        }}
        title="Cancel this attempt?"
        subtitle="This marks the attempt as cancelled. It won't count toward the student's attempt quota, so they can start a fresh attempt."
      >
        <div className="space-y-3">
          <Textarea
            label="Reason (required)"
            placeholder="e.g. Network disconnected, attempt auto-submitted within seconds"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
          />
          <p className="text-xs text-white/40">
            The reason + your user ID will be recorded in audit_logs.
          </p>
        </div>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setCancelModalOpen(false);
              setCancelReason('');
            }}
            disabled={cancelMutation.isPending}
          >
            Keep attempt
          </Button>
          <Button
            variant="danger"
            onClick={() => cancelMutation.mutate(cancelReason.trim())}
            disabled={!cancelReason.trim() || cancelMutation.isPending}
            isLoading={cancelMutation.isPending}
          >
            Cancel attempt
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

function calculateGrade(percentage: number): string {
  if (percentage >= 90) return 'A+';
  if (percentage >= 85) return 'A';
  if (percentage >= 80) return 'A-';
  if (percentage >= 75) return 'B+';
  if (percentage >= 70) return 'B';
  if (percentage >= 65) return 'B-';
  if (percentage >= 60) return 'C+';
  if (percentage >= 55) return 'C';
  if (percentage >= 50) return 'D';
  return 'F';
}
